/* ==========================================================================================
   Commercial Equipment Online 2026 · ce-gst.js · the EX GST / INC GST switch (foundation, 09-10-2026)
   A port of the CORE of the live theme's assets/gst-toggle.js (A307c / A350 / A724), without the Empire-specific
   product-page furniture (delivery panel, buy row, quote button: those belong to the product page builder).

   THE RULES (kept from live):
     * KEY 'rb-gst-mode' in localStorage, values 'ex' | 'inc'. Opens on the theme default (live: ex). The live site
       uses the SAME key, so a returning shopper keeps their choice on the new theme.
     * <html data-gst-mode="ex|inc"> is set before the first paint by a tiny script in layout/theme.liquid.
     * PRODUCT prices follow the switch; the BASKET never does (the cart is what gets charged, inc GST). A price
       inside the cart drawer, the cart page, a cart form or [data-cart] / [data-ce-cart] is never converted.
     * A node's text is one of three things: the cached inc figure (data-gst-inc), that figure net of GST, or a NEW
       figure someone else wrote (a variant change). Only the third is news; it is always inc GST and becomes the
       cached figure before anything is converted (A724: never write a stale variant price back).
     * Labels: [data-gst-label] say "EX GST" / "INC GST"; a card price gets a small sibling tag (.ce-gsttag) that says
       which basis the number is on (A350: "the qualifier has to sit where the number sits").

   HOW A BUILDER USES IT
     * Any element with class .money or [data-gst-price] whose text is an INC-GST money figure is converted.
       Prefer printing BOTH figures and letting CSS choose (no script needed, no flicker):
         <span class="ce-gst-ex">{{ price | divided_by: 1.1 | money }}</span><span class="ce-gst-inc">{{ price | money }}</span>
       html[data-gst-mode="ex"] .ce-gst-inc and html[data-gst-mode="inc"] .ce-gst-ex are hidden (ce-header.css).
     * The switch: any element with [data-gst-toggle] (role=button); its two halves carry [data-opt="ex"|"inc"].
     * Events: document 'ce:gst:change' (detail.mode) after every flip; window.ceGst.mode() / .set('ex'|'inc').
   ========================================================================================== */
(() => {
  'use strict';
  if (window.ceGst) return;
  const KEY = 'rb-gst-mode';
  const RATE = 1.1;
  const H = document.documentElement;
  const fallback = H.getAttribute('data-gst-mode') === 'inc' ? 'inc' : 'ex';
  const mode = () => {
    let m = '';
    try { m = localStorage.getItem(KEY) || ''; } catch (e) { /* private mode */ }
    return m === 'ex' || m === 'inc' ? m : fallback;
  };
  const store = (m) => { try { localStorage.setItem(KEY, m); } catch (e) { /* private mode */ } };

  const CART = '.cart, cart-drawer, cart-items, .cart-drawer, #CartDrawer, form[action*="/cart"], [data-cart], [data-ce-cart], .ce-cart, .totals, .cart-notification, #cart-notification';
  const excluded = (el) => !!(el.closest && el.closest(CART));

  const fmt = (n, sample) => {
    const parts = n.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const cur = (sample.match(/^[^0-9]*/) || [''])[0];
    const tail = (sample.match(/[^0-9]*$/) || [''])[0];
    return cur + parts.join('.') + tail;
  };
  const exOf = (incText) => {
    const num = parseFloat(incText.replace(/[^0-9.]/g, ''));
    return isFinite(num) ? fmt(num / RATE, incText) : null;
  };

  // card prices get a small "ex GST" / "inc GST" tag beside them (A350); the cart's always say inc GST
  const CARD_HOST = '[data-gst-tag]';
  const tagOn = (host, word) => {
    let tag = null;
    for (let i = 0; i < host.children.length; i++) if (host.children[i].className === 'ce-gsttag') tag = host.children[i];
    if (!tag) { tag = document.createElement('span'); tag.className = 'ce-gsttag'; host.appendChild(tag); }
    if (tag.textContent !== word) tag.textContent = word;
  };

  const labels = () => {
    const ex = mode() === 'ex';
    document.querySelectorAll('[data-gst-label]').forEach((l) => {
      const w = ex ? (l.dataset.ex || 'EX GST') : (l.dataset.inc || 'INC GST');
      if (l.textContent !== w) l.textContent = w;
    });
    document.querySelectorAll('[data-gst-toggle]').forEach((b) => {
      b.setAttribute('data-mode', ex ? 'ex' : 'inc');
      b.querySelectorAll('[data-opt]').forEach((o) => o.setAttribute('aria-pressed', (o.dataset.opt === 'ex') === ex ? 'true' : 'false'));
    });
  };

  const convert = (root) => {
    const ex = mode() === 'ex';
    H.setAttribute('data-gst-mode', ex ? 'ex' : 'inc');
    (root || document).querySelectorAll('.money, [data-gst-price]').forEach((el) => {
      if (excluded(el)) return;
      const cur = (el.textContent || '').trim();
      let inc = el.getAttribute('data-gst-inc');
      if (inc === null) {
        if (!/\d/.test(cur)) return;
        el.setAttribute('data-gst-inc', cur);
        inc = cur;
      } else if (cur !== inc && cur !== exOf(inc) && /\d/.test(cur)) {
        el.setAttribute('data-gst-inc', cur);   // A724: the theme wrote a new figure (variant change): learn it
        inc = cur;
      }
      const want = ex ? exOf(inc) : inc;
      if (want !== null && el.textContent !== want) el.textContent = want;
    });
    document.querySelectorAll(CARD_HOST).forEach((h) => { if (!excluded(h)) tagOn(h, ex ? 'ex GST' : 'inc GST'); });
    labels();
  };

  const set = (m) => {
    if (m !== 'ex' && m !== 'inc') return;
    store(m);
    convert();
    document.dispatchEvent(new CustomEvent('ce:gst:change', { detail: { mode: m } }));
  };
  const toggle = () => set(mode() === 'ex' ? 'inc' : 'ex');

  document.addEventListener('click', (e) => {
    const b = e.target.closest && e.target.closest('[data-gst-toggle]');
    if (!b) return;
    e.preventDefault();
    const opt = e.target.closest('[data-opt]');
    // pressing a half chooses it; pressing the thumb or the box flips
    if (opt && (opt.dataset.opt === 'ex' || opt.dataset.opt === 'inc') && opt.dataset.opt !== mode()) set(opt.dataset.opt);
    else if (!opt) toggle();
  });
  // the halves are real buttons (Enter / Space press them); the arrow keys move between the two as well
  document.addEventListener('keydown', (e) => {
    const b = e.target.closest && e.target.closest('[data-gst-toggle]');
    if (!b) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const m = e.key === 'ArrowLeft' ? 'ex' : 'inc';
      set(m);
      const o = b.querySelector(`[data-opt="${m}"]`);
      if (o && o.focus) o.focus();
    }
  });
  // another tab changed it: follow
  window.addEventListener('storage', (e) => { if (e.key === KEY) convert(); });

  window.ceGst = { mode, set, toggle, convert };

  const boot = () => {
    convert();
    // variant switches, predictive search, quick add and section re-renders write prices: convert again, debounced.
    // Write-only-on-change (above) means our own writes cause no endless loop.
    if (window.MutationObserver) {
      let t = 0;
      new MutationObserver(() => { clearTimeout(t); t = setTimeout(() => convert(), 60); })
        .observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
